import { useEffect, useLayoutEffect } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useMatch,
} from 'react-router-dom';
import { BrandNavbar } from '@/components/BrandNavbar';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { capturePageview, isAdminPath, setAdminMode } from '@/lib/analytics';
import { AccessCodeEntry } from '@/routes/AccessCodeEntry';
import { AdminConsole } from '@/routes/AdminConsole';
import { ProspectCanvas } from '@/routes/ProspectCanvas';

export function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

/**
 * Inside the router, so it can read the current location.
 *
 * Exported for tests: App hardcodes BrowserRouter, so this is the seam.
 */
export function Shell() {
  const location = useLocation();
  const onCanvas = useMatch('/d/:accessCode') !== null;

  /**
   * Admin mode is derived from the location, not from AdminConsole's lifecycle,
   * and set in a LAYOUT effect so it is committed before the passive pageview
   * effect below.
   *
   * Route-driven is what makes it self-clearing. A flag that only ever turned ON
   * would silently kill analytics for the rest of the tab's life, and a live
   * meeting that captures nothing while looking completely normal is the worst
   * failure mode this app has.
   */
  useLayoutEffect(() => {
    setAdminMode(isAdminPath(location.pathname));
  }, [location.pathname]);

  /* PostHog is configured with capture_pageview: false, because the automatic
     one fires only on first load and this is a single-page app. */
  useEffect(() => {
    capturePageview(location.pathname);
  }, [location.pathname]);

  return (
    <div className="app-shell">
      <BrandNavbar showHistory={onCanvas} />
      <Routes>
        <Route path="/" element={<AccessCodeEntry />} />
        <Route path="/d/:accessCode" element={<ProspectCanvas />} />
        {/* One URL for the whole console: unlocking, the list and editing a
            scenario are phases of this route, never navigations. */}
        <Route path="/admin" element={<AdminConsole />} />
        {/* Generic: an unknown path reveals nothing about what exists. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}
